import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../../hooks/useDatabase';
import { usePublicLanding } from '../../hooks/usePublicContent';
import { submitLead } from '../../services/leadService';
import { readOrigin } from '../../utils/utm';
import { isUuid, logFailure } from '../../utils/diagnostics';
import { LandingRenderer } from './LandingRenderer';
import { DynamicFormResult } from './DynamicForm';
import { Button } from '../ui/Button';
import { Form, LandingPage } from '../../types';
interface LandingViewProps {
  slug: string;
  /** Muestra los accesos a la plataforma en la barra superior (home de AVA). */
  showAppNav?: boolean;
  /** Contenido estático usado si la landing no existe en la base de datos. */
  fallback?: {
    landing: LandingPage;
    form?: Form;
  };
}
export function LandingView({
  slug,
  showAppNav = false,
  fallback
}: LandingViewProps) {
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const loading = usePublicLanding(slug);
  const landing = db.landings.find(l => l.slug === slug) ?? fallback?.landing;
  const organization = landing ? db.organizations.find(o => o.id === landing.organizationId) : undefined;
  const form = (landing?.formId ? db.forms.find(f => f.id === landing.formId) : undefined) ?? fallback?.form;
  if (loading && !landing) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-558" data-mp="17wo8hgaqmvy5">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-559" data-mp="u62nnm1q4bxm" />
        <span className="sr-only" data-id="element-560" data-mp="y6mfqb7fwpcw">Cargando la página…</span>
      </main>;
  }
  if (!landing) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-561" data-mp="k6tsipv4gr9o">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-562" data-mp="p795hgbhrwr4" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-563" data-mp="2qw5aai4yopl5">Esta página no existe</h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-564" data-mp="2m32es4gmj62b">
          La landing que buscas no está publicada o el enlace ha cambiado.
        </p>
        <Link to="/" data-id="element-565" data-mp="2d35x4msnj7gr">
          <Button variant="secondary" data-id="element-566" data-mp="2d3peo2ee3tu9">Volver al inicio</Button>
        </Link>
      </main>;
  }

  // El logotipo de la landing hereda el de la empresa si no se ha definido uno.
  const resolved = {
    ...landing,
    pageConfig: {
      ...landing.pageConfig,
      logoUrl: landing.pageConfig.logoUrl || organization?.brand.logoUrl || organization?.logoUrl
    }
  };
  async function handleSubmit(result: DynamicFormResult) {
    if (!landing || !form) return;
    setError(null);

    // La plantilla estática de respaldo tiene ids que no son UUID: si se está
    // mostrando esa, no existe en la base de datos y no puede crear leads.
    if (!isUuid(form.id) || !isUuid(landing.id)) {
      logFailure('submitLead', {
        message: 'Se está mostrando la landing de ejemplo incluida en el código, no una landing real de la base de datos.',
        details: `landing_id="${landing.id}", form_id="${form.id}", slug="${slug}"`,
        hint: 'Publica tu landing desde el panel y visita su URL pública (/landing/tu-slug).'
      }, {
        landingId: landing.id,
        formId: form.id,
        slug
      });
      const message = 'Esta es la landing de ejemplo del sistema y no está conectada a ninguna empresa, por lo que no puede guardar contactos. Publica tu landing desde el panel y usa su enlace público.';
      setError(message);
      throw new Error(message);
    }
    try {
      await submitLead({
        organizationId: landing.organizationId,
        formId: form.id,
        landingId: landing.id,
        contact: result.contact,
        answers: result.answers,
        origin: readOrigin(location.search, {
          landingId: landing.id,
          formId: form.id
        })
      });
      // Flujo previsto: formulario multipaso → cualificación → calendario.
      // Si la landing tiene bloque de calendario, el visitante continúa ahí
      // sin salir de la página; si no, va a la página de gracias.
      const hasCalendar = landing.pageConfig.blocks.some(block => block.type === 'calendario' && block.visible);
      if (hasCalendar) {
        document.getElementById('calendario')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return;
      }
      navigate(`/gracias/${landing.slug}`);
    } catch (err) {
      // El mensaje real se propaga al formulario, que lo muestra en línea.
      setError(err instanceof Error ? err.message : 'No hemos podido enviar tu solicitud.');
      throw err;
    }
  }
  return <main className="w-full bg-background" data-id="element-567" data-mp="11mo2bj7znwpw">
      {error && <p role="alert" className="bg-red-50 px-5 py-3 text-center text-sm text-red-700" data-id="element-568" data-mp="11l4l9qeidhn2">
          {error}
        </p>}
      <LandingRenderer landing={resolved} form={form} onSubmit={handleSubmit} authLinks={showAppNav} data-id="element-569" data-mp="wm0boyno9hw7" />
    </main>;
}